/**
 * Bug report modal component.
 */

import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';

interface BugReportModalProps {
  show: boolean;
  onHide: () => void;
}

export default function BugReportModal({ show, onHide }: BugReportModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotFilename, setScreenshotFilename] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTakeScreenshot = async () => {
    try {
      const canvas = await html2canvas(document.body);
      const dataUrl = canvas.toDataURL('image/png');
      setScreenshot(dataUrl);
      setScreenshotFilename('screenshot.png');
      toast.success(t('modals.bugReport.screenshotCaptured'));
    } catch (error) {
      toast.error(t('modals.bugReport.failedCapture'));
      console.error('Screenshot error:', error);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('modals.bugReport.fileTooLarge'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshot(reader.result as string);
        setScreenshotFilename(file.name);
        toast.success(t('modals.bugReport.fileUploaded'));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    setScreenshotFilename('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (title.length < 5) {
      toast.error(t('modals.bugReport.titleTooShort'));
      return;
    }
    if (description.length < 10) {
      toast.error(t('modals.bugReport.descriptionTooShort'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.submitBugReport(
        title,
        description,
        priority,
        screenshot || undefined,
        screenshotFilename || undefined
      );

      if (response.error) {
        toast.error(response.error);
        return;
      }

      toast.success(t('modals.bugReport.submitted'));
      handleClose();
    } catch (error) {
      toast.error(t('modals.bugReport.failedSubmit'));
      console.error('Bug report error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setPriority('medium');
    setScreenshot(null);
    setScreenshotFilename('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{t('modals.bugReport.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>{t('modals.bugReport.bugTitle')}</Form.Label>
          <Form.Control
            type="text"
            placeholder={t('modals.bugReport.titlePlaceholder')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Form.Text className="text-muted">{t('modals.bugReport.titleMinChars')}</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{t('modals.bugReport.description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            placeholder={t('modals.bugReport.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Form.Text className="text-muted">{t('modals.bugReport.descriptionMinChars')}</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{t('modals.bugReport.priority')}</Form.Label>
          <Form.Select value={priority} onChange={(e) => setPriority(e.target.value)}>
            <option value="low">{t('modals.bugReport.low')}</option>
            <option value="medium">{t('modals.bugReport.medium')}</option>
            <option value="high">{t('modals.bugReport.high')}</option>
            <option value="critical">{t('modals.bugReport.critical')}</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>{t('modals.bugReport.screenshot')}</Form.Label>
          {screenshot && (
            <div className="mb-2">
              <img
                src={screenshot}
                alt={t('modals.bugReport.screenshotPreview')}
                style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}
              />
              <div className="mt-2">
                <small className="text-muted me-2">{screenshotFilename}</small>
                <Button variant="danger" size="sm" onClick={handleRemoveScreenshot}>
                  {t('modals.bugReport.remove')}
                </Button>
              </div>
            </div>
          )}
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" onClick={handleTakeScreenshot}>
              <i className="fas fa-camera me-2"></i>
              {t('modals.bugReport.takeScreenshot')}
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="fas fa-upload me-2"></i>
              {t('modals.bugReport.uploadFile')}
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.png,.jpg,.jpeg,.gif,.bmp"
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <Form.Text className="text-muted">{t('modals.bugReport.maxSize')}</Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {t('common.submit')}
        </Button>
        <Button variant="secondary" onClick={handleClose}>
          {t('common.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
